import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logoDarkPurple from '../imports/1_(3).svg';
import { sendMagicLinkEmail, type BeatlyApiError } from '@/api/beatlyApi';
import { BEATLY_PRIVACY_POLICY_URL } from '@/config/legal';
import { isValidEmail } from '@/utils/validation';
import { showError, showSuccess } from '@/utils/alert';

interface LoginPageProps {
  onClose?: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsLoading(true);
    try {
      const res = await sendMagicLinkEmail({ email: trimmed });
      if (res.code === 'SENT_LOGIN_MAGICLINK') {
        setLinkSent(true);
      }
    } catch (err) {
      const apiErr = err as BeatlyApiError;
      if (apiErr.code === 'USER_NOT_FOUND' || apiErr.status === 404) {
        navigate(`/signup?email=${encodeURIComponent(trimmed)}`);
        return;
      }
      showError(t('login.errorGeneric'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsLoading(true);
    sendMagicLinkEmail({ email: trimmed })
      .then(() => showSuccess(t('login.emailResent')))
      .catch(() => showError(t('login.errorGeneric')))
      .finally(() => setIsLoading(false));
  };

  const handleUseDifferentEmail = () => {
    setLinkSent(false);
    setEmail('');
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-auto"
      style={{ background: '#f8f9fa', animation: 'loginFadeIn 300ms ease-out' }}
    >
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-white transition-all z-10"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div
        className="w-full max-w-[420px] mx-4"
        style={{ animation: 'loginContentIn 500ms ease-out' }}
      >
        <div className="flex justify-center mb-8">
          <Link to="/">
            <img src={logoDarkPurple} alt="Beatly" className="h-9 w-auto" />
          </Link>
        </div>

        <div
          className="bg-white rounded-[24px] px-10 py-10"
          style={{
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.06), 0 12px 48px rgba(0,0,0,0.04)',
          }}
        >
          {!linkSent ? (
            <>
              <div className="text-center mb-8">
                <h1
                  className="text-[#101828] text-[28px] mb-2"
                  style={{ fontFamily: 'Instrument Sans, sans-serif', fontWeight: 600 }}
                >
                  {t('login.title')}
                </h1>
                <p className="text-[#667085] text-[15px] leading-relaxed">{t('login.subtitle')}</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label
                    className="block text-[13px] text-[#344054] mb-1.5"
                    style={{ fontWeight: 500 }}
                  >
                    {t('login.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-white text-[15px] text-[#101828] placeholder:text-[#d0d5dd] outline-none rounded-xl border transition-all duration-200"
                    style={{
                      borderColor: focusedField === 'email' ? '#be43ff' : '#e4e7ec',
                      boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(190,67,255,0.08)' : 'none',
                    }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl text-white text-[15px] transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70"
                  style={{
                    fontWeight: 600,
                    background: '#be43ff',
                    boxShadow: '0 1px 2px rgba(190,67,255,0.2)',
                  }}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    // Keep a React-owned element when browser translation replaces its text node.
                    <span>{t('login.button')}</span>
                  )}
                </button>
              </form>

              <p className="text-center text-[14px] text-[#667085] mt-6">
                <span>{t('login.noAccount')}{' '}</span>
                <Link to="/signup" className="text-[#be43ff] hover:text-[#a830e0]" style={{ fontWeight: 600 }}>
                  {t('login.signUp')}
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-5">
                <Mail className="w-8 h-8 text-[#101828]" />
              </div>
              <h2 className="text-[#101828] text-[18px] mb-2 text-center" style={{ fontWeight: 600 }}>
                {t('login.checkEmailTitle')}
              </h2>
              <p className="text-[#667085] text-[14px] text-center mb-5">
                {t('login.checkEmailSubtitle', { email })}
              </p>
              <p className="text-[#667085] text-[13px] text-center mb-4">{t('login.checkEmailReminder')}</p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isLoading}
                  className="text-[#be43ff] hover:text-[#a830e0] text-[14px] font-medium disabled:opacity-70"
                >
                  {t('login.resendLink')}
                </button>
                <button
                  type="button"
                  onClick={handleUseDifferentEmail}
                  className="text-[#667085] hover:text-[#344054] text-[14px]"
                >
                  {t('login.useDifferentEmail')}
                </button>
              </div>
              <p className="text-center text-[14px] text-[#667085] mt-6">
                <span>{t('login.noAccount')}{' '}</span>
                <Link to="/signup" className="text-[#be43ff] hover:text-[#a830e0]" style={{ fontWeight: 600 }}>
                  {t('login.signUp')}
                </Link>
              </p>
            </>
          )}
        </div>

        <p className="text-center text-[12px] text-[#98a2b3] mt-6 leading-relaxed max-w-[300px] mx-auto">
          {t('login.privacyNote')}{' '}
          <a
            href={BEATLY_PRIVACY_POLICY_URL}
            target="_blank"
            rel="noreferrer"
            className="text-[#667085] hover:text-[#344054] underline underline-offset-2 transition-colors"
          >
            {t('login.privacyPolicy')}
          </a>
        </p>
      </div>

      <style>{`
        @keyframes loginFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes loginContentIn {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
